import api from './api';
import { FirewallFarbe } from '../utils/firewallReview';

export interface FirewallBewertung {
  mitarbeiter_id: number;
  name: string;
  initialen?: string;
  farbe: FirewallFarbe;
  text: string;
  nach_konsens: boolean;
}

export interface FirewallKonsens {
  farbe: FirewallFarbe;
  text: string;
  gesetzt_von_id?: number | null;
  gesetzt_am?: string | null;
}

export interface FirewallIpWiderspruch {
  aufgeloest: string;
  im_namen: string;
}

export interface FirewallAdresseAufgeloest {
  name: string;
  aufgeloest: boolean;
  typ?: string | null;
  wert?: string | null;
  werte: string[];
  interface?: string | null;
  grund?: string | null;
  ist_all: boolean;
  ip_widerspruch?: FirewallIpWiderspruch | null;
  mapped_from?: string | null;
}

export interface FirewallInterfaceSeite {
  name: string;
  aufgeloest: boolean;
  typ?: string | null;
  ip?: string | null;
  members: string[];
  grund?: string | null;
}

export interface FirewallServiceInfo {
  raw: string;
  label: string;
  name?: string | null;
  quelle?: string | null;
  protokoll?: string | null;
  port_von?: number | null;
  port_bis?: number | null;
  bereiche: string[];
  auffaellig: boolean;
  risiko: boolean;
  risiko_grund?: string | null;
  weitere_accept: number;
  objekt?: string | null;
  gruppe?: string | null;
  name_widerspruch?: { behauptet: string; geoeffnet: string } | null;
}

export interface FirewallPoolAufgeloest {
  name: string;
  aufgeloest: boolean;
  wert?: string | null;
  werte: string[];
  typ?: string | null;
}

export interface FirewallAnalyse {
  bytes_wert?: number | null;
  nie_genutzt: boolean;
  bucket?: number | null;
  perzentil_audit?: number | null;
  perzentil_vdom?: number | null;
  inspection_fehlt: boolean;
  log_abweichend: boolean;
  schedule_abweichend: boolean;
  typ_abweichend: boolean;
  nat_aktiv: boolean;
}

export interface FirewallStatistik {
  regeln: number;
  nie_genutzt: number;
  verteilung: number[];
  accept: number;
  accept_ohne_inspection: number;
  accept_ohne_inspection_prozent?: number | null;
  log_nicht_all: number;
  log_nicht_all_prozent?: number | null;
  schedule_abweichend: number;
  nat: number;
}

export type FirewallRisikoCode =
  | 'any'
  | 'wildcard_service'
  | 'risikoport'
  | 'ohne_inspection'
  | 'log'
  | 'nie_genutzt'
  | 'kein_kommentar'
  | 'deaktiviert'
  | 'schedule'
  | 'nat';

export interface FirewallRisiko {
  code: FirewallRisikoCode | string;
  label: string;
  grund: string;
  stufe: 'rot' | 'gelb';
}

export interface FirewallRegel {
  id: number;
  upload_id: number;
  vdom?: string | null;
  paket?: string | null;
  position: number;
  policy_id: string;
  anzeigename: string;
  policy_raw: string;
  source: string[];
  destination: string[];
  schedule: string[];
  service: string[];
  action: string;
  ip_pool: string[];
  ip_pool_aufgeloest?: FirewallPoolAufgeloest[];
  nat: string;
  regel_typ: string;
  security_profiles: string[];
  log: string;
  bytes: string;
  bytes_wert?: number | null;
  bytes_ist_null: boolean;
  interface_pair: string;
  firewall_kommentar: string;
  policy_status?: string;
  abschnitt: string;
  inhalt_geaendert: boolean;
  entfernt: boolean;
  source_aufgeloest: FirewallAdresseAufgeloest[];
  destination_aufgeloest: FirewallAdresseAufgeloest[];
  interface_quelle?: FirewallInterfaceSeite | null;
  interface_ziel?: FirewallInterfaceSeite | null;
  services: FirewallServiceInfo[];
  analyse: FirewallAnalyse;
  risiken: FirewallRisiko[];
  ist_any: boolean;
  eigene_bewertung?: FirewallBewertung | null;
  bewertungen: FirewallBewertung[];
  konsens?: FirewallKonsens | null;
  weicht_vom_final_ab: boolean;
}

export type FirewallUploadTyp =
  | 'policies'
  | 'adressen'
  | 'adressgruppen'
  | 'services'
  | 'servicegruppen'
  | 'vips'
  | 'vipgruppen'
  | 'ippools'
  | 'interfaces';

export interface FirewallVdom {
  firewall_id: number;
  vdom?: string | null;
  label: string;
  bewertet: number;
  gesamt: number;
  dateien: Partial<Record<FirewallUploadTyp, boolean>>;
  statistik: FirewallStatistik;
  regeln: FirewallRegel[];
}

/** Eine Firewall in der Übersicht: aggregierter Fortschritt plus ihre VDOM-Blöcke. */
export interface FirewallGeraet {
  id: number;
  name: string;
  sort_order: number;
  bewertet: number;
  gesamt: number;
  statistik: FirewallStatistik;
  vdoms: FirewallVdom[];
}

/** Firewall-Verwaltung und Vorschlagsliste: nur Name, Reihenfolge und VDOM-Namen. */
export interface FirewallGeraetKurz {
  id: number;
  name: string;
  sort_order: number;
  vdoms: (string | null)[];
  vdom_labels: string[];
}

/** `firewall_id` → bestehende Firewall; `neu` → neue Firewall mit `name`; beides leer → Auswahl Pflicht. */
export interface FirewallVorschlag {
  firewall_id?: number | null;
  neu: boolean;
  name?: string | null;
  grund: string;
}

export interface FirewallUploadVorschlag {
  typ: FirewallUploadTyp;
  format_id: string;
  vdom?: string | null;
  vdom_label: string;
  vorschlag: FirewallVorschlag;
  geraete: FirewallGeraetKurz[];
}

export interface FirewallVdomVerschoben {
  uploads: number;
  regeln: number;
  adressen: number;
  interfaces: number;
}

export interface FirewallGeraetLoeschung {
  uploads: number;
  regeln: number;
  bewertungen: number;
  konsens: number;
  objekte: number;
}

export interface FirewallUpload {
  id: number;
  firewall_id: number;
  dateiname: string;
  typ: FirewallUploadTyp;
  format_id: string;
  format_label: string;
  hersteller?: string | null;
  vdom?: string | null;
  vdom_label: string;
  paket?: string | null;
  status: string;
  regel_anzahl: number;
  erstellt_am?: string | null;
  regeln_aktuell: number;
  bewertungen_anzahl: number;
  konsens_anzahl: number;
  objekt_anzahl: number;
  nachtrag?: boolean;
  zugeordnet?: number;
  nicht_zugeordnet?: string[];
}

export interface FirewallTeamMitglied {
  id: number;
  name: string;
  initialen: string;
}

export interface FirewallOverview {
  ist_firewall_audit: boolean;
  ansicht: 'uebersicht' | 'swipe' | 'final';
  darf_bewerten: boolean;
  sprache: string;
  uploads: FirewallUpload[];
  team: FirewallTeamMitglied[];
  statistik: FirewallStatistik;
  geraete: FirewallGeraet[];
}

export interface FirewallVorlage {
  id: number;
  button_label_de: string;
  button_label_en: string;
  text_de: string;
  text_en: string;
  sort_order: number;
  aktiv: boolean;
}

export interface FirewallRisikoPort {
  id: number;
  protokoll: 'tcp' | 'udp' | 'any' | string;
  port_von: number;
  port_bis: number;
  grund: string;
  aktiv: boolean;
}

export interface FirewallLoeschung {
  regeln: number;
  bewertungen: number;
  konsens: number;
  objekte: number;
}

export type FirewallAnsicht = 'uebersicht' | 'swipe' | 'final';

export interface FirewallUploadOptions {
  vdom?: string | null;
  /** Bestehende Firewall; hat Vorrang vor `firewallName`. */
  firewallId?: number | null;
  /** Firewall per Name anlegen oder wiederverwenden. */
  firewallName?: string | null;
}

function uploadForm(file: File, options: FirewallUploadOptions): FormData {
  const form = new FormData();
  form.append('file', file);
  if (options.vdom && options.vdom.trim().length > 0) form.append('vdom', options.vdom.trim());
  if (options.firewallId != null) form.append('firewall_id', String(options.firewallId));
  else if (options.firewallName && options.firewallName.trim().length > 0) form.append('firewall_name', options.firewallName.trim());
  return form;
}

export const firewallService = {
  getOverview: async (auditId: number, ansicht: FirewallAnsicht = 'uebersicht'): Promise<FirewallOverview> => {
    const response = await api.get(`/audits/${auditId}/firewall`, { params: { ansicht } });
    return response.data;
  },

  listVdoms: async (auditId: number): Promise<string[]> => {
    const response = await api.get(`/audits/${auditId}/firewall/vdoms`);
    return response.data;
  },

  /** Prüft eine Datei, ohne sie zu speichern: Typ, VDOM und Firewall-Vorschlag mit Grund. */
  vorschlag: async (auditId: number, file: File, vdom?: string | null): Promise<FirewallUploadVorschlag> => {
    const response = await api.post(`/audits/${auditId}/firewall/uploads/vorschlag`, uploadForm(file, { vdom }));
    return response.data;
  },

  upload: async (auditId: number, file: File, options: FirewallUploadOptions = {}): Promise<FirewallUpload> => {
    const response = await api.post(`/audits/${auditId}/firewall/uploads`, uploadForm(file, options));
    return response.data;
  },

  listGeraete: async (auditId: number): Promise<FirewallGeraetKurz[]> => {
    const response = await api.get(`/audits/${auditId}/firewall/geraete`);
    return response.data;
  },

  createGeraet: async (auditId: number, name: string, sortOrder?: number | null): Promise<FirewallGeraetKurz> => {
    const response = await api.post(`/audits/${auditId}/firewall/geraete`, {
      name,
      ...(sortOrder != null ? { sort_order: sortOrder } : {}),
    });
    return response.data;
  },

  updateGeraet: async (
    auditId: number,
    firewallId: number,
    data: { name?: string; sort_order?: number },
  ): Promise<FirewallGeraetKurz> => {
    const response = await api.put(`/audits/${auditId}/firewall/geraete/${firewallId}`, data);
    return response.data;
  },

  geraetLoeschvorschau: async (auditId: number, firewallId: number): Promise<FirewallGeraetLoeschung> => {
    const response = await api.get(`/audits/${auditId}/firewall/geraete/${firewallId}/loeschvorschau`);
    return response.data;
  },

  deleteGeraet: async (auditId: number, firewallId: number): Promise<FirewallGeraetLoeschung> => {
    const response = await api.delete(`/audits/${auditId}/firewall/geraete/${firewallId}`);
    return response.data;
  },

  /** Verschiebt einen VDOM samt Uploads, Regeln, Adressen, Interfaces; Bewertungen und Konsens bleiben. */
  moveVdom: async (
    auditId: number,
    firewallId: number,
    vdom: string,
    zielFirewallId: number,
  ): Promise<FirewallVdomVerschoben> => {
    const response = await api.put(
      `/audits/${auditId}/firewall/geraete/${firewallId}/vdoms/${encodeURIComponent(vdom)}/verschieben`,
      { ziel_firewall_id: zielFirewallId },
    );
    return response.data;
  },

  deleteUpload: async (auditId: number, uploadId: number): Promise<FirewallLoeschung> => {
    const response = await api.delete(`/audits/${auditId}/firewall/uploads/${uploadId}`);
    return response.data;
  },

  saveBewertung: async (
    auditId: number,
    regelId: number,
    farbe: FirewallFarbe,
    text: string,
  ): Promise<FirewallRegel> => {
    const response = await api.put(`/audits/${auditId}/firewall/regeln/${regelId}/bewertung`, { farbe, text });
    return response.data;
  },

  saveKonsens: async (
    auditId: number,
    regelId: number,
    farbe: FirewallFarbe,
    text: string,
  ): Promise<FirewallRegel> => {
    const response = await api.put(`/audits/${auditId}/firewall/regeln/${regelId}/konsens`, { farbe, text });
    return response.data;
  },

  listVorlagen: async (alle = false): Promise<FirewallVorlage[]> => {
    const response = await api.get('/firewall/vorlagen', { params: { alle } });
    return response.data;
  },

  createVorlage: async (data: Omit<FirewallVorlage, 'id'>): Promise<FirewallVorlage> => {
    const response = await api.post('/firewall/vorlagen', data);
    return response.data;
  },

  updateVorlage: async (id: number, data: Omit<FirewallVorlage, 'id'>): Promise<FirewallVorlage> => {
    const response = await api.put(`/firewall/vorlagen/${id}`, data);
    return response.data;
  },

  deleteVorlage: async (id: number): Promise<void> => {
    await api.delete(`/firewall/vorlagen/${id}`);
  },

  listRisikoports: async (): Promise<FirewallRisikoPort[]> => {
    const response = await api.get('/firewall/risikoports');
    return response.data;
  },

  createRisikoport: async (data: Omit<FirewallRisikoPort, 'id'>): Promise<FirewallRisikoPort> => {
    const response = await api.post('/firewall/risikoports', data);
    return response.data;
  },

  updateRisikoport: async (id: number, data: Omit<FirewallRisikoPort, 'id'>): Promise<FirewallRisikoPort> => {
    const response = await api.put(`/firewall/risikoports/${id}`, data);
    return response.data;
  },

  deleteRisikoport: async (id: number): Promise<void> => {
    await api.delete(`/firewall/risikoports/${id}`);
  },
};
